import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { MuiOtpInput } from 'mui-one-time-password-input';
import {
  sendOTP,
  verifyOTP,
  verifySponsorEmail,
  addSponsoredUsers,
  getAthenaKeywords,
} from '../api/athena4p-api';
import { IKeyword } from '../models/IKeyword';
import KeywordSearchDropdown from './KeywordSearchDropdown';
import cachedKeywordsData from '../assets/keywords.json';

interface IUserRow {
  id: number;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  keywords?: IKeyword[];
}

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onAddRow: () => void;
  }
}

const GroupForm = () => {
  const [userRecords, setUserRecords] = useState<IUserRow[]>([]);
  const [sponsorEmail, setSponsorEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [adminRowKey, setAdminRowKey] = useState('');
  const [openOtpDialog, setOpenOtpDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpStatusMessage, setOtpStatusMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [step, setStep] = useState<'verify' | 'otp' | 'complete'>('verify');
  const apiRef = useGridApiRef();

  const [keywordsData, setKeywordsData] = useState<IKeyword[]>([]);
  const [selectedKeywords, setSelectedKeywords] = useState<IKeyword[]>([]);

  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [clickedUserId, setClickedUserId] = useState<number | null>(null);

  const init = () => {
    const rows = Array.from(
      { length: 15 },
      (_, index) =>
        ({
          id: index + 1,
          firstName: '',
          middleName: '',
          lastName: '',
          email: '',
          keywords: [],
        } as IUserRow)
    );

    setUserRecords(rows);
  };

  useEffect(init, []);

  useEffect(() => {
    loadKeywordsData();
  }, []);

  const loadKeywordsData = async () => {
    try {
      const response = await getAthenaKeywords();
      if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
        setKeywordsData(response.data);
      } else {
        throw new Error('Invalid keywords response');
      }
    } catch {
      setKeywordsData(cachedKeywordsData as IKeyword[]);
    }
  };

  const onAddRow = () => {
    const lastRow = userRecords[userRecords.length - 1];
    const newRow = {
      id: !userRecords.length ? 1 : lastRow.id + 1,
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      keywords: [],
    };

    setUserRecords((prev) => {
      const updated = [...prev, newRow];
      setTimeout(() => {
        if (apiRef && apiRef.current) {
          apiRef?.current.scrollToIndexes({ rowIndex: updated.length - 1 });
          apiRef?.current.setCellFocus(updated.length, 'firstName');
        }
      }, 0);
      return updated;
    });
  };

  const onDeleteRow = (id: number) => {
    setUserRecords((prev) => prev.filter((row) => row.id !== id));
  };

  const handleSelectUser = (userId: number, checked: boolean) => {
    setSelectedUserIds((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const filledUserIds = userRecords
        .filter((row) => row.firstName || row.lastName || row.email)
        .map((row) => row.id);
      setSelectedUserIds(filledUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleKeywordsChange = (keywords: IKeyword[]) => {
    setSelectedKeywords(keywords);
  };

  const handleTagUsers = () => {
    if (selectedKeywords.length === 0) return;

    const targetUserIds =
      selectedUserIds.length > 0
        ? selectedUserIds
        : clickedUserId !== null
          ? [clickedUserId]
          : [];

    if (targetUserIds.length === 0) return;

    setUserRecords((prev) =>
      prev.map((row) => {
        if (!targetUserIds.includes(row.id)) {
          return row;
        }
        const existingKeywordIds = new Set(
          (row.keywords || []).map((k) => k.keywordId)
        );
        const newKeywords = selectedKeywords.filter(
          (k) => !existingKeywordIds.has(k.keywordId)
        );
        return {
          ...row,
          keywords: [...(row.keywords || []), ...newKeywords],
        };
      })
    );

    if (selectedUserIds.length > 0) {
      setSelectedUserIds([]);
    }
    setSelectedKeywords([]);
  };

  const getTagButtonLabel = (): string => {
    if (selectedUserIds.length > 0) {
      return `Tag Selected (${selectedUserIds.length})`;
    }
    if (clickedUserId !== null) {
      return `Tag ${getClickedUserName()}`;
    }
    return 'Tag Users';
  };

  const isTagButtonDisabled = (): boolean => {
    const hasTargetUsers = selectedUserIds.length > 0 || clickedUserId !== null;
    return !hasTargetUsers || selectedKeywords.length === 0;
  };

  const handleRemoveKeyword = (keywordId: number) => {
    if (clickedUserId !== null) {
      setUserRecords((prev) =>
        prev.map((row) =>
          row.id === clickedUserId
            ? {
                ...row,
                keywords: row.keywords?.filter((k) => k.keywordId !== keywordId),
              }
            : row
        )
      );
    }
  };

  const handleRowClick = (params: any) => {
    const hasData =
      params.row.firstName || params.row.lastName || params.row.email;
    if (hasData) {
      setClickedUserId(params.row.id);
    } else {
      setClickedUserId(null);
    }
  };

  const getClickedUserKeywords = (): IKeyword[] => {
    if (clickedUserId !== null) {
      const user = userRecords.find((r) => r.id === clickedUserId);
      return user?.keywords || [];
    }
    return [];
  };

  const getClickedUserName = (): string => {
    if (clickedUserId !== null) {
      const user = userRecords.find((r) => r.id === clickedUserId);
      if (user) {
        const name = `${user.firstName}`.trim();
        return name || user.email || 'Selected User';
      }
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!sponsorEmail) {
      setError('Please enter sponsor email address');
      return;
    }

    const validUsers = userRecords.filter(
      (row) =>
        row.firstName.trim() !== '' &&
        row.lastName.trim() !== '' &&
        row.email.trim() !== ''
    );
    if (validUsers.length === 0) {
      setError(
        'Please add at least one sponsored user with First Name, Last Name, and Email'
      );
      return;
    }

    setLoading(true);

    await verifySponsorEmail({ sponsorEmail: sponsorEmail })
      .then(async (verifyResponse) => {
        if (verifyResponse && verifyResponse.status === 200) {
          const verifyData = verifyResponse.data;

          if (!verifyData.RowKey) {
            throw new Error('Invalid response: missing rowKey');
          }

          setAdminRowKey(verifyData.RowKey);

          const sendOTPData = {
            email: sponsorEmail,
          };

          await sendOTP(sendOTPData)
            .then((sendOTPResponse) => {
              if (sendOTPResponse && sendOTPResponse.status === 200) {
                setAlertSeverity('success');
                setOtpStatusMessage(
                  `OTP sent to ${sponsorEmail}. If not received, please check your SPAM folder before resending.`
                );
                setStep('otp');
                setOpenOtpDialog(true);
              }
            })
            .catch((_error) => {
              setAlertSeverity('error');
              setOtpStatusMessage(`Failed to send OTP to ${sponsorEmail}`);
              setError(`Failed to send OTP to ${sponsorEmail}`);
            });
        }
      })
      .catch((err) => {
        setError(
          'Failed to verify admin email. Please ensure you are using a valid admin email.'
        );
        console.error('Verify admin error:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOtpSubmit = async () => {
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    const verifyOTPData = {
      email: sponsorEmail,
      otp: otp,
    };

    await verifyOTP(verifyOTPData)
      .then(async (verifyOTPResponse) => {
        if (verifyOTPResponse && verifyOTPResponse.status === 200) {
          // Only include users with required fields, and map to ISponsoredUser format
          const validUsers = userRecords
            .filter(
              (row) =>
                row.firstName.trim() !== '' ||
                row.lastName.trim() !== '' ||
                row.email.trim() !== ''
            )
            .map((row) => ({
              firstName: row.firstName.trim(),
              middleName: row.middleName?.trim() || '',
              lastName: row.lastName.trim(),
              email: row.email.trim(),
              keywordIds: row.keywords?.length
                ? row.keywords.map((k) => k.keywordId).join(' ')
                : undefined,
              keywordsText: row.keywords?.length
                ? row.keywords.map((k) => k.title).join(';')
                : undefined,
            }));

          const addUsersData = {
            adminRowKey: adminRowKey,
            sponsoredUsers: validUsers,
          };

          console.log(addUsersData);

          await addSponsoredUsers(addUsersData)
            .then((addUsersResponse) => {
              if (addUsersResponse && addUsersResponse.status === 200) {
                setStep('complete');
                setAlertSeverity('success');
                setSuccess('Users successfully added to the database!');
                setTimeout(() => {
                  setOpenOtpDialog(false);
                  setSponsorEmail('');
                  setOtp('');
                  init();
                  setStep('verify');
                  setOtpStatusMessage('');
                }, 3000);
              }
            })
            .catch((_error) => {
              setError('Failed to add users to database. Please try again.');
            });
        }
      })
      .catch((_error) => {
        setError('Invalid OTP. Please check and try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCloseDialog = () => {
    if (!loading) {
      setOpenOtpDialog(false);
      setOtp('');
      setError('');
      setOtpStatusMessage('');
      setStep('verify');
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    setOtpStatusMessage('');
    setOtp('');

    const sendOTPData = {
      email: sponsorEmail,
    };

    await sendOTP(sendOTPData)
      .then((sendOTPResponse) => {
        if (sendOTPResponse && sendOTPResponse.status === 200) {
          setAlertSeverity('success');
          setOtpStatusMessage(
            `OTP resent to ${sponsorEmail}. If not received, please check your SPAM folder.`
          );
        }
      })
      .catch((_error) => {
        setAlertSeverity('error');
        setOtpStatusMessage(`Failed to resend OTP to ${sponsorEmail}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const filledUsersCount = userRecords.filter(
    (r) => r.firstName || r.lastName || r.email
  ).length;

  const columns: GridColDef[] = [
    {
      field: 'select',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Checkbox
            size='small'
            checked={
              selectedUserIds.length > 0 &&
              selectedUserIds.length === filledUsersCount
            }
            indeterminate={
              selectedUserIds.length > 0 &&
              selectedUserIds.length < filledUsersCount
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </Box>
      ),
      renderCell: (params) => {
        const hasData =
          params.row.firstName || params.row.lastName || params.row.email;
        return hasData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Checkbox
              size='small'
              checked={selectedUserIds.includes(params.row.id)}
              onChange={(e) => handleSelectUser(params.row.id, e.target.checked)}
            />
          </Box>
        ) : null;
      },
    },
    {
      field: 'firstName',
      headerName: 'First Name *',
      editable: true,
      flex: 1,
      sortable: false,
    },
    {
      field: 'middleName',
      headerName: 'Middle Name',
      editable: true,
      flex: 1,
      sortable: false,
    },
    {
      field: 'lastName',
      headerName: 'Last Name *',
      editable: true,
      flex: 1,
      sortable: false,
    },
    {
      field: 'email',
      headerName: 'Email *',
      editable: true,
      flex: 1,
      sortable: false,
      renderHeader: () => (
        <Box>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography variant='subtitle2'>Email *</Typography>
            <Tooltip title='Add New Row'>
              <IconButton onClick={onAddRow}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ),
    },
    {
      field: 'keywords',
      headerName: 'Keywords',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const count = params.row.keywords?.length || 0;
        return count > 0 ? (
          <Tooltip
            title={params.row.keywords
              .map((k: IKeyword) => k.title)
              .join(', ')}
          >
            <Chip
              icon={<LocalOfferIcon />}
              label={count}
              size='small'
              color='primary'
            />
          </Tooltip>
        ) : (
          <Typography variant='caption' color='textSecondary'>
            -
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title='Delete Row'>
          <IconButton
            size='small'
            color='error'
            onClick={() => onDeleteRow(params.row.id)}
          >
            <DeleteIcon fontSize='small' color='primary' />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();

    if (fileName.endsWith('.csv')) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results: any) => {
          const parsedRows = results.data.map((row: any, index: number) => ({
            id: index + 1,
            firstName: row.firstName || row['First Name'] || '',
            middleName: row.middleName || row['Middle Name'] || '',
            lastName: row.lastName || row['Last Name'] || '',
            email: row.email || row['Email'] || row.usersEmailAddress || '',
            keywords: [],
          }));
          setUserRecords(parsedRows);
        },
      });
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet);
        const parsedRows = json.map((row: any, index: number) => ({
          id: index + 1,
          firstName: row.firstName || row['First Name'] || '',
          middleName: row.middleName || row['Middle Name'] || '',
          lastName: row.lastName || row['Last Name'] || '',
          email: row.email || row['Email'] || row.usersEmailAddress || '',
          keywords: [],
        }));
        setUserRecords(parsedRows);
      };
      reader.readAsArrayBuffer(file);
    } else {
      alert('Unsupported file format');
    }
  };

  return (
    <Box mt={2}>
      <form onSubmit={handleSubmit}>
        <TextField
          label='Sponsors Email Address'
          name='sponsorEmail'
          value={sponsorEmail}
          onChange={(e) => setSponsorEmail(e.target.value)}
          fullWidth
          required
          margin='normal'
          variant='outlined'
          helperText='Please use your official organization email for verification purposes.'
          type='email'
        />

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <KeywordSearchDropdown
              keywordsData={keywordsData}
              getSelectedKeywords={handleKeywordsChange}
              label='Keywords (Optional)'
              initialValue={selectedKeywords}
            />
          </Box>
          <Button
            variant='contained'
            startIcon={<LocalOfferIcon />}
            disabled={isTagButtonDisabled()}
            onClick={handleTagUsers}
            sx={{ mt: 1.5 }}
          >
            {getTagButtonLabel()}
          </Button>
        </Box>
        <Typography variant='caption' color='textSecondary' sx={{ ml: 1 }}>
          Select users via checkboxes or click a row, choose keywords, then click the button to apply.
        </Typography>

        {error && !openOtpDialog && (
          <Alert severity='error' sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && !openOtpDialog && (
          <Alert severity='success' sx={{ mt: 2, mb: 2 }}>
            {success}
          </Alert>
        )}

        <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
          Press Enter or double click to edit and save
        </Typography>

        <DataGrid
          apiRef={apiRef}
          columns={columns}
          rows={userRecords}
          getRowId={(row) => row.id}
          editMode='cell'
          hideFooter
          rowHeight={30}
          sx={{ height: 400 }}
          disableVirtualization
          onRowClick={handleRowClick}
          processRowUpdate={(newRow) => {
            setUserRecords((prev) =>
              prev.map((row) => (row.id === newRow.id ? newRow : row))
            );
            return newRow;
          }}
        />

        <Box display='flex' alignItems={'center'} gap={2} pt={2}>
          <Box pt={2}>
            <Button
              variant='contained'
              component='label'
              startIcon={<UploadIcon />}
            >
              Upload CSV/Excel
              <input
                type='file'
                accept='.csv,.xlsx, .xls'
                hidden
                onChange={handleFileUpload}
              />
            </Button>

            <Link
              href='/template.csv'
              underline='hover'
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                display: 'block',
                fontSize: '0.75rem',
                color: 'text.secondary',
                mt: 0.5,
              }}
            >
              Download Template
            </Link>
          </Box>

          <Button type='submit' variant='contained' disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </Box>

        {clickedUserId !== null && getClickedUserKeywords().length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
              Keywords for {getClickedUserName()}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {getClickedUserKeywords().map((keyword) => (
                <Chip
                  key={keyword.keywordId}
                  icon={<LocalOfferIcon />}
                  label={keyword.title}
                  size='small'
                  color='primary'
                  variant='outlined'
                  onDelete={() => handleRemoveKeyword(keyword.keywordId)}
                />
              ))}
            </Box>
          </Box>
        )}
      </form>

      <Dialog
        open={openOtpDialog}
        onClose={(_event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseDialog();
          }
        }}
        maxWidth='sm'
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          {step === 'otp' && 'Email Verification'}
          {step === 'complete' && 'Success!'}
        </DialogTitle>
        <DialogContent>
          {step === 'otp' && (
            <>
              <Typography variant='body2' sx={{ mb: 3 }}>
                We've sent a 6-digit verification code to {sponsorEmail}. Please
                enter it below to continue.
              </Typography>

              {otpStatusMessage && (
                <Alert severity={alertSeverity} sx={{ mb: 2 }}>
                  {otpStatusMessage}
                </Alert>
              )}

              <MuiOtpInput
                value={otp}
                onChange={setOtp}
                length={6}
                autoFocus
                TextFieldsProps={{
                  disabled: loading,
                  placeholder: '-',
                }}
                sx={{ gap: 1 }}
              />

              {error && (
                <Alert severity='error' sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant='text'
                  onClick={handleResendOTP}
                  disabled={loading}
                  size='small'
                >
                  Resend OTP
                </Button>
              </Box>
            </>
          )}

          {step === 'complete' && <Alert severity='success'>{success}</Alert>}
        </DialogContent>
        <DialogActions>
          {step === 'otp' && (
            <>
              <Button onClick={handleCloseDialog} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleOtpSubmit}
                variant='contained'
                disabled={loading || otp.length !== 6}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupForm;
